import { motion } from 'motion/react';
import React, { PropsWithChildren } from 'react';

import QuatrefoilShape from '~/assets/vector/quatrefoil.svg';
import {
  MouseCoords,
  useMotionMousePosition,
} from '~/lib/hooks/useMousePosition';
import { cn } from '~/lib/utils/classname';

type Slots =
  | 'main'
  | 'outerContainer'
  | 'innerContainer'
  | 'shapeContainer'
  | 'shape'
  | 'text';

type Props = {
  className?: string;
  classNames?: { [key in Slots]?: string };
  visible?: boolean;
  followCursor?: boolean;
  offsetFromCursor?: MouseCoords;
} & PropsWithChildren;

const QuatrefoilFloater: React.FC<Props> = ({
  className,
  classNames,
  visible,
  followCursor = false,
  offsetFromCursor = { x: 0, y: 0 },
  children,
}) => {
  const { x, y } = useMotionMousePosition({
    offset: offsetFromCursor,
    disabled: !followCursor,
  });

  return (
    <div
      className={cn(
        'pointer-events-none fixed top-0 left-0 z-10 h-full w-full overflow-clip',
        className,
        classNames?.main
      )}
      data-slot="main"
    >
      <motion.div
        className={cn('absolute', classNames?.outerContainer)}
        initial={false}
        animate={{
          opacity: visible ? 1 : 0,
          rotate: visible ? 0 : -180,
          scale: visible ? 1 : 0.5,
          transition: { duration: 0.6, ease: 'circInOut' },
        }}
        style={{ x, y }}
        data-slot="outerContainer"
      >
        <div
          className={cn(
            'text-brand-tertiary absolute top-0 left-0 flex aspect-square h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center text-center',
            classNames?.innerContainer
          )}
          data-slot="innerContainer"
        >
          <motion.div
            className={cn(
              'absolute top-0 left-0 h-full w-full',
              classNames?.shapeContainer
            )}
            animate={
              visible
                ? {
                    rotate: [0, 360],
                    transition: {
                      duration: 10,
                      ease: 'easeInOut',
                      repeat: Infinity,
                      repeatType: 'loop',
                    },
                  }
                : undefined
            }
            data-slot="shapeContainer"
          >
            <QuatrefoilShape
              className={cn(
                'text-brand-primary h-full w-full',
                classNames?.shape
              )}
              data-slot="shape"
            />
          </motion.div>

          <div
            className={cn('relative text-xs', classNames?.text)}
            data-slot="text"
          >
            {children}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default QuatrefoilFloater;
